
import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, GeoJSON, useMapEvent } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import africa from '../../json/africa_boundaries.json'
import iso_metrics from '../../json/iso_metrics.json'
import countryMapping from '../../json/countries.json'
import country_metrics from '../../json/country_metrics.json'
import * as ContextMenu from '@radix-ui/react-context-menu';
import 'react-flagpack/dist/style.css'
import '../../index.css';

/**
 * Mapping of download speeds to their corresponding hex colors.
 * 
 * @constant {Object} downloadSpeedToHexColor
 * - "0-5": Bright Red (#FF6F6F)
 * - "5-10": Bright Peach (#FF9A5B)
 * - "10-20": Bright Yellow (#F9F93F)
 * - "20-50": Bright Green (#9CFE9F)
 * - "50-100": Light Green (#66C466)
 * - "100+": Dark Green (#4CAF50)
 */
const downloadSpeedToHexColor = {
  "0-5": "#FF6F6F",       // Brighter Red
  "5-10": "#FF9A5B",      // Brighter Peach
  "10-20": "#F9F93F",     // Brighter Yellow
  "20-50": "#9CFE9F",     // Brighter Green
  "50-100": "#66C466",    // Brighter Light Green
  "100+": "#4CAF50"       // Brighter Green
};

const reversedMapping = {};
for (const [code, name] of Object.entries(countryMapping)) {
  reversedMapping[name] = code;
}

/**
 * Creates an object that maps countries to their colors based on the provided metric.
 * 
 * @param {string} metric - The metric used for color determination ("average", "download", or "upload").
 * @returns {Object} An object where each key is a country code and the value is a hex color.
 */
function createCountryColorsObject(metric) {
  var object = {}
  for (var item in country_metrics) {
    if (metric === "average") {
      object[countryMapping[item]] = getHexColorForSpeed((country_metrics[item].average_download + country_metrics[item].average_upload) / 2)
    }
    else if (metric === "download") {
      object[countryMapping[item]] = getHexColorForSpeed((country_metrics[item].average_download))
    }
    else if (metric === "upload") {
      object[countryMapping[item]] = getHexColorForSpeed((country_metrics[item].average_upload))
    }
    else {
      object[countryMapping[item]] = getHexColorForSpeed((country_metrics[item].average_download + country_metrics[item].average_upload) / 2)
    }
  }
  object["Somalia"] = object["Somaliland"]
  return object

}


/**
 * Creates an object that maps provinces to their colors based on the provided metric.
 * 
 * @param {string} metric - The metric used for color determination ("average", "download", or "upload").
 * @returns {Object} An object where each key is a province code and the value is a hex color.
 */
function createProvinceColorsObject(metric) {
  let p_object = {}
  for (var item in iso_metrics) {
    if (metric === "average") {
      p_object[item] = getHexColorForSpeed((parseInt(iso_metrics[item][0]) + parseInt(iso_metrics[item][1])) / 2)
    }
    else if (metric === "download") {
      p_object[item] = getHexColorForSpeed((parseInt(iso_metrics[item][0])))
    }

    else if (metric === "upload") {
      p_object[item] = getHexColorForSpeed((parseInt(iso_metrics[item][1])))
    }
    else {
      p_object[item] = getHexColorForSpeed((parseInt(iso_metrics[item][0]) + parseInt(iso_metrics[item][1])) / 2)
    }
  }
  return p_object;
}

/**
 * Determines the hex color based on the provided download/upload speed.
 * 
 * @param {number} speed - The internet speed in Mbps.
 * @returns {string} The corresponding hex color based on speed ranges.
 */
function getHexColorForSpeed(speed) {
  if (speed === null) return "#808080"; // Gray for missing data
  if (speed < 5) return downloadSpeedToHexColor["0-5"];
  if (speed < 10) return downloadSpeedToHexColor["5-10"];
  if (speed < 20) return downloadSpeedToHexColor["10-20"];
  if (speed < 50) return downloadSpeedToHexColor["20-50"];
  if (speed < 100) return downloadSpeedToHexColor["50-100"];
  return downloadSpeedToHexColor["100+"];
}

function SetViewOnClick() {
  const map = useMapEvent('click', (e) => {
    const layer = e.target; // Access the layer that was clicke
    const bounds = layer.getBounds(); // Get the bounds of the clicked feature
    map.fitBounds(bounds)
  })

  return null
}

/**
 * Fetches country-specific GeoJSON data and sets it as the focused data.
 * 
 * @param {Object} feature - The clicked country's feature object.
 * @param {function} setFocusedData - Callback to update the focused data state.
 */
function onCountryClick(feature, setFocusedData) {

  fetch(`https://cadesayner.pythonanywhere.com/getCountryGeoJson?country=${feature.properties.NAME}`)
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.json(); // Parse JSON data from the response
    })
    .then(data => {
      setFocusedData(data);
    })
    .catch(error => {
      console.error('There was a problem with the fetch operation:', error);
    });
}

/**
 * Calculates the centroid of a polygon based on its coordinates.
 * 
 * @param {Array} polygonCoords - An array of [longitude, latitude] coordinates.
 * @returns {Array} The [longitude, latitude] coordinates of the centroid.
 */
function calculatePolygonCentroid(polygonCoords) {
  let x = 0;
  let y = 0;
  let numPoints = 0;

  polygonCoords[0].forEach(([lng, lat]) => {
    x += lng;
    y += lat;
    numPoints += 1;
  });

  return [x / numPoints, y / numPoints];
}


/**
 * The Map component that renders the map, handles country and province clicks, and updates map styles based on selected metrics.
 * 
 * @param {string} metric - The metric used for coloring the map ("average", "download", or "upload").
 * @param {function} countryClickCallback - Callback function for handling country clicks.
 * @param {function} provinceClickCallback - Callback function for handling province clicks.
 * @param {function} leaderboardCallback - Callback function for displaying leaderboards.
 */
function Map({ metric, countryClickCallback, provinceClickCallback, leaderboardCallback }) {
  // State to store the currently focused GeoJSON data for the selected feature on the map.
  const [focusedData, setFocusedData] = useState(null);

  // State to hold color mappings for each country based on the selected metric.
  const [countryColors, setCountryColors] = useState({});

  // State to hold color mappings for each province based on the selected metric.
  const [focusedColors, setFocusedColors] = useState({});

  // State to track the currently selected feature on the map (e.g., country or province).
  const [selectedFeature, setSelectedFeature] = useState(null);

  // State used to force a re-render of certain components or map features when necessary.
  const [force, setForce] = useState(false);

  // State to control whether the context menu should be displayed.
  const [canDisplayContextMenu, setCanDisplayContext] = useState(true);

  // State to keep track of the number of features clicked for context menu interactions.
  const [featureCounter, setFeatureCounter] = useState(0);

  // State to keep track of the number of context menu interactions.
  const [contextCounter, setContextCounter] = useState(0);

  // Ref to hold the current value of contextCounter, used to compare with featureCounter.
  const countRef = useRef(contextCounter);

  // State to store the feature currently selected in the context menu.
  const [contextMenuFeature, setCMFeature] = useState(null);

  // State to store the type of context menu (e.g., COUNTRY or REGION).
  const [contextMenuType, setContextMenuType] = useState(null);

  // State to store the administrative region associated with the context menu feature.
  const [contextMenuAdmin, setCMAdmin] = useState(null);

  // State to keep track of the screen width, used for responsive design.
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);


  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Function to define the style of the GeoJSON features for provinces.
  // The fill color is based on the province's color mapping from the `focusedColors` state.
  // If no color is available, it defaults to 'grey'.
  const provinceGeoJsonStyle = (feature) => ({
    fillColor: focusedColors[feature.properties.iso_3166_2] || 'grey',
    color: 'black',  // Border color of the province
    weight: 0.5,     // Border width
    fillOpacity: 0.65, // Fill opacity
    opacity: 0.5,    // Overall opacity of the feature
  });

  // Function to handle context menu options based on user selection.
  function contextMenuHandler(option) {
    // Handle "Add" option based on the context menu type
    if (option === "Add") {
      if (contextMenuType === "COUNTRY") {
        // Callback for country selection
        countryClickCallback(contextMenuFeature);
      } else if (contextMenuType === "REGION") {
        // Callback for region selection
        provinceClickCallback(contextMenuAdmin, contextMenuFeature);
      }
    }
    // Handle "ISP-Leaderboard" option
    else if (option === "ISP-Leaderboard") {
      // Callback to display the ISP leaderboard
      leaderboardCallback(contextMenuType, contextMenuFeature, "ISP");
    }
    // Handle "Region-Leaderboard" option
    else if (option === "Region-Leaderboard") {
      // Callback to display the region leaderboard
      leaderboardCallback(contextMenuType, contextMenuFeature, "REGION");
    } else {
      // Handle invalid context menu selection
      console.error("Invalid context menu selection.");
    }
  }

  // Function to increment the feature counter and update the state.
  function updateFeatureCounter() {
    setFeatureCounter(countRef.current + 1);
  }


  var countryGeoJsonStyle = (feature) => ({
    fillColor: countryColors[feature.properties.NAME] || 'grey',
    color: 'black',
    weight: feature.properties.NAME === selectedFeature ? 2 : 0.5,
    opacity: feature.properties.NAME === selectedFeature ? 1 : 0.5,
    fillOpacity: feature.properties.NAME === selectedFeature ? 0 : 0.65
  })

  useEffect(() => {
    let c_colors = createCountryColorsObject(metric)
    setCountryColors(c_colors);
    let p_colors = createProvinceColorsObject(metric);
    setFocusedColors(p_colors);
  },
    [metric]);

  useEffect(() => {
    countRef.current = contextCounter
  },
    [contextCounter]);

  useEffect(() => {
  },
    [screenWidth]);

  useEffect(() => {
    if (contextCounter === featureCounter) {
      setCanDisplayContext(true);
    }
    else {
      setCanDisplayContext(false);
    }
  },
    [contextCounter, featureCounter]);

  useEffect(() => {
    setForce(!force)

  }, [focusedData])

  const onEachFeature = (feature, layer) => {
    layer.on({
      click: (e) => {
        let centroid = [0, 0]
        if (feature.geometry.type === "Polygon") {
          centroid = calculatePolygonCentroid(feature.geometry.coordinates)
        }

        else if (feature.geometry.type === "MultiPolygon") {
          centroid = calculatePolygonCentroid(feature.geometry.coordinates[0])
        }
        let map = e.sourceTarget._map
        map.setView({ lat: centroid[1], lng: centroid[0] }, 5.2)
        if (feature !== selectedFeature) {
          if (feature.properties.NAME !== "Eswatini" && feature.properties.NAME !== "S. Sudan" && feature.properties.NAME !== "Somaliland") {
            onCountryClick(feature, setFocusedData);
            setSelectedFeature(feature.properties.NAME) // set the selected feature here
          }
        }

      },

      mousedown: (e) => {
        if (e.originalEvent.button === 2) {
          // Right click:
          updateFeatureCounter()
          setCMFeature(feature.properties.NAME)
          setContextMenuType("COUNTRY")
        }
      },
    });

    const tooltipContent = `<div class="leaflet-tooltip-style">
            <strong>${feature.properties.NAME ? feature.properties.NAME : feature.properties.name}</strong>
            </div>`;
    layer.bindTooltip(tooltipContent, {
      permanent: false,
      direction: 'auto',
      className: 'leaflet-tooltip-style'
    });
  };

  const onEachFeatureProvince = (feature, layer) => {
    layer.on({

      mousedown: (e) => {
        if (e.originalEvent.button === 1) {
          if (iso_metrics[feature.properties.iso_3166_2]) {
            provinceClickCallback(feature.properties.admin, feature.properties.name) // send data back so that it can populate the cards 
          }
        }
        if (e.originalEvent.button === 2) {
          // Right click:
          updateFeatureCounter();
          setCMFeature(feature.properties.name);
          setCMAdmin(feature.properties.admin !== "Democratic Republic of the Congo" ? feature.properties.admin : "Dem. Rep. Congo");
          setContextMenuType("REGION");
        }
      },

      mouseover: (e) => {
        layer.setStyle({ fillOpacity: 0.8 });

      },
      mouseout: (e) => {
        const layer = e.target;
        layer.setStyle({
          fillOpacity: 0.65,
        });
      }
    });

    const tooltipContent = `<div class="leaflet-tooltip-style"><strong>${feature.properties.NAME ? feature.properties.NAME : feature.properties.name}</strong> <br>
           <p>Download Speed: ${iso_metrics[feature.properties.iso_3166_2] ? parseFloat(iso_metrics[feature.properties.iso_3166_2][0]).toFixed(2) : "No data"}</p>
           <p>Upload Speed: ${iso_metrics[feature.properties.iso_3166_2] ? parseFloat(iso_metrics[feature.properties.iso_3166_2][1]).toFixed(2) : "No data"}</p>
           </div>`;
    layer.bindTooltip(tooltipContent, {
      permanent: false,
      direction: 'auto',
      className: 'leaflet-tooltip-style'
    });
  };
  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger onContextMenu={() => setContextCounter(contextCounter + 1)} >
        <MapContainer zoomControl={false} center={[20, 16]} maxBounds={screenWidth > 1440 ? [
          [-40, -40], // South-West corner
          [40, 75],   // North-East corner
        ] : [
          [-40, -40], // South-West corner
          [50, 75],   // North-East corner
        ]}

          zoom={screenWidth > 1440 ? 4 : 3} minZoom={screenWidth > 1440 ? 4 : 3} maxZoom={10} style={{ position: "fixed", height: "100vh", width: "100%" }}>

          <GeoJSON
            onEachFeature={(feature, layer) => onEachFeature(feature, layer)}
            data={africa}
            style={countryGeoJsonStyle}
          />

          {focusedData && (<GeoJSON
            key={force}
            style={provinceGeoJsonStyle}
            onEachFeature={(feature, layer) => onEachFeatureProvince(feature, layer)}
            data={focusedData} />)}

          {
            <TileLayer
              url="https://api.maptiler.com/maps/basic-v2/{z}/{x}/{y}.png?key=NX3QDTlTJcKS9eKWCLUy"
              attribution='&copy; <a href="https://www.maptiler.com/copyright">MapTiler</a>'
            />
          }

          <SetViewOnClick />
        </MapContainer>
      </ContextMenu.Trigger>
      <ContextMenu.Portal>
        {canDisplayContextMenu && (<ContextMenu.Content className='ContextMenuContent'>
          <ContextMenu.Item className="ContextMenuItem" onSelect={() => { contextMenuHandler("Add") }}>
            Add to Comparison
          </ContextMenu.Item>
          <ContextMenu.Item className="ContextMenuItem" onSelect={() => { contextMenuHandler("ISP-Leaderboard") }}>
            ISP Rankings
          </ContextMenu.Item>
          <ContextMenu.Separator />
          {contextMenuType === "COUNTRY" && <ContextMenu.Item className="ContextMenuItem" onSelect={() => contextMenuHandler("Region-Leaderboard")}>
            Region Rankings
          </ContextMenu.Item>}
        </ContextMenu.Content>)}
      </ContextMenu.Portal>
    </ContextMenu.Root>
  );
}

export default Map